import IconPin from "@/components/icons/icon-pin";
import IconAnonymous from "@/components/icons/icon-anonymous";
import Tooltip, { TooltipProvider } from "@/components/ui/tooltip";
import type { Review } from "@/modules/review/types";

interface ReviewSelectItemProps {
	review: Review;
	isPinned: boolean;
	onToggleStar: () => void;
	disabled?: boolean;
}

// Helper to render star rating
function StarRating({ rating }: { rating: number }) {
	return (
		<div className="flex items-center gap-0.5">
			{[1, 2, 3, 4, 5].map((star) => (
				<svg key={star} className={`w-3 h-3 ${star <= rating ? "text-amber-400" : "text-gray-200"}`} fill="currentColor" viewBox="0 0 20 20">
					<path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
				</svg>
			))}
		</div>
	);
}

// Helper to get content type badge
function ContentTypeBadge({ contentType }: { contentType: string }) {
	const badgeConfig: Record<string, { label: string; className: string }> = {
		video: { label: "Video", className: "bg-purple-100 text-purple-700" },
		audio: { label: "Audio", className: "bg-blue-100 text-blue-700" },
		text: { label: "Text", className: "bg-gray-100 text-gray-600" },
	};

	const config = badgeConfig[contentType] || badgeConfig.text;

	return <span className={`text-xs px-1.5 py-0.5 rounded ${config.className}`}>{config.label}</span>;
}

export default function ReviewSelectItem({ review, isPinned, onToggleStar, disabled = false }: ReviewSelectItemProps) {
	// Get display content
	const isAnonymous = review.consent === "anonymous";
	const displayName = review.customer_name || "No name";
	const displayContent = review.content_text || "";
	const truncatedContent = displayContent.length > 100 ? displayContent.slice(0, 100) + "..." : displayContent;

	// Generate avatar background gradient if no avatar
	const avatarGradient = `hsl(${(displayName.charCodeAt(0) * 137) % 360}, 70%, 60%)`;

	return (
		<div className="flex items-start gap-3 p-3 bg-white rounded-lg border border-gray-200 hover:border-gray-300 transition-colors">
			{/* Avatar */}
			<div className="shrink-0">
				{review.customer_avatar_url ? (
					<img src={review.customer_avatar_url} alt={displayName} className="w-10 h-10 rounded-full object-cover" />
				) : (
					<div className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-medium" style={{ backgroundColor: avatarGradient }}>
						{displayName.charAt(0).toUpperCase()}
					</div>
				)}
			</div>

			{/* Content */}
			<div className="flex-1 min-w-0">
				<div className="flex items-center gap-2 mb-1">
					<span className={`text-sm font-medium truncate ${isAnonymous ? "italic text-gray-400" : "text-gray-900"}`}>{displayName}</span>
					{isAnonymous && (
						<TooltipProvider>
							<Tooltip content="This review is set to anonymous — personal details will be hidden from the public.">
								<IconAnonymous className="w-3.5 h-3.5 text-gray-400 shrink-0 cursor-help" />
							</Tooltip>
						</TooltipProvider>
					)}
					{review.rating > 0 && <StarRating rating={review.rating} />}
					<ContentTypeBadge contentType={review.content_type} />
				</div>
				<p className="text-sm text-tertiary line-clamp-2">{truncatedContent}</p>
				{review.customer_job_title && review.customer_company_name && (
					<p className="text-xs text-tertiary mt-1">
						{review.customer_job_title} at {review.customer_company_name}
					</p>
				)}
			</div>

			{/* Star button */}
			<button
				type="button"
				onClick={onToggleStar}
				disabled={disabled && !isPinned}
				className={`shrink-0 p-1.5 rounded-md transition-colors ${
					isPinned ? "text-gray-500 hover:text-gray-600 hover:bg-gray-50" : disabled ? "text-gray-300 cursor-not-allowed" : "text-gray-400 hover:text-gray-500 hover:bg-gray-50"
				}`}
				aria-label={isPinned ? "Remove from starred" : "Add to starred"}
				title={disabled && !isPinned ? "Maximum 10 starred reviews" : undefined}
			>
				<IconPin solid={isPinned} />
			</button>
		</div>
	);
}
